import type { ComponentProps } from 'react';
import { makeStyles } from 'vendor/material';
import {
  fontWeightMedium,
  fontWeightMediumLarge,
} from 'utils/fontWeightConstants';
import { Tag as BaseTag } from 'packages/cosell/src/components/Tag/Tag';

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'grid',
    paddingInline: theme.spacing(1),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    height: 'max-content',
    flexWrap: 'wrap',
    '& > dl': {
      flex: '1 1 250px',
    },
    borderBottom: `1px solid ${theme.palette.NEUTRAL030}`,
    paddingBlock: theme.spacing(2),
  },
  detail: {
    color: theme.palette.NEUTRAL800,
    display: 'grid',
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(24),
    gridTemplateRows: 'auto 1fr',
    gap: theme.spacing(0.5),
    margin: 0,
    overflow: 'hidden',
  },
  title: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: fontWeightMediumLarge,
    lineHeight: theme.typography.pxToRem(20),
    color: theme.palette.NEUTRAL300,
    margin: 0,
  },
  value: {
    margin: 0,
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  tag: {
    '& span': {
      fontSize: `12px !important`,
      fontWeight: fontWeightMedium,
      color: theme.palette.NEUTRAL700,
      letterSpacing: '0.3px',
    },
  },
}));

/**
 * DetailsGrid component with additional subcomponents for layout structure.
 */
interface DetailsGridComponent extends React.FC<{ children: React.ReactNode }> {
  /**
   * Container for grouping related detail items in a row.
   * Provides consistent spacing and layout.
   */
  Row: React.FC<{ children: React.ReactNode }>;

  /**
   * Displays a single detail item with a title and value.
   * Use within a DetailsGrid.Row component.
   *
   * @param props.title - The label text displayed above the detail value
   * @param props.children - The content/value to display below the title
   */
  Detail: React.FC<{ title: React.ReactNode; children: React.ReactNode }>;

  /**
   * A styled tag component for use within detail values.
   * Ensures consistent styling within the DetailsGrid context.
   */
  Tag: React.FC<ComponentProps<typeof BaseTag>>;
}

/**
 * DetailsGrid is a compound component for displaying structured data in a grid layout.
 * It provides a consistent way to show titled detail items organized in rows.
 *
 * @component
 * @example
 * ```tsx
 * <DetailsGrid>
 *   <DetailsGrid.Row>
 *     <DetailsGrid.Detail title="Status">Active</DetailsGrid.Detail>
 *     <DetailsGrid.Detail title="Type">
 *       <DetailsGrid.Tag>Premium</DetailsGrid.Tag>
 *     </DetailsGrid.Detail>
 *   </DetailsGrid.Row>
 * </DetailsGrid>
 * ```
 */
const DetailsGrid: DetailsGridComponent = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.content}>{children}</div>;
};

const Row: DetailsGridComponent['Row'] = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.row}>{children}</div>;
};

const Detail: DetailsGridComponent['Detail'] = ({ children, title }) => {
  const classes = useStyles();
  return (
    <dl data-testid="detail" className={classes.detail}>
      <dt className={classes.title}>{title}</dt>
      <dd className={classes.value}>{children}</dd>
    </dl>
  );
};

const Tag: DetailsGridComponent['Tag'] = ({ children }) => {
  const classes = useStyles();
  return (
    <span className={classes.tag}>
      <BaseTag>{children}</BaseTag>
    </span>
  );
};

DetailsGrid.Row = Row;
DetailsGrid.Detail = Detail;
DetailsGrid.Tag = Tag;

export default DetailsGrid;
